import { createSelector, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { DateTime } from "luxon";

import { AppThunk } from "appThunk";
import { selectAppDetails } from "modules/appDetails/appDetailsSlice";
import { selectUserInfo } from "modules/auth/authSlice";
import {
    selectLikelihoodOfVisitingThresholds,
    selectSharedAndNewCustomersByCustomerProfile,
    selectSharedPopulationByStore,
    selectIsLoading as selectCannibalisationIsLoading,
    selectHasErrors as selectCannibalisationHasErrors
} from "modules/customer/tools/location/cannibalisation/cannibalisationSlice";
import { selectBreakdownOfCompetitorStores } from "modules/customer/tools/location/competition/competitionSlice";
import {
    selectCatchmentAreaDemographic,
    selectDemographicIndicatorsInCatchmentArea,
    selectTotalPopulationInCatchmentArea,
    selectWeightedPopulationInCatchmentArea,
    selectIsLoading as selectDemographicsIsLoading,
    selectHasErrors as selectDemographicsHasErrors
} from "modules/customer/tools/location/demographics/demographicsSlice";
import {
    selectLastTwelveMonthsTotalVisits,
    selectLatestMonthFootfall,
    selectIsLoading as selectFootfallIsLoading,
    selectHasErrors as selectFootfallHasErrors
} from "modules/customer/tools/location/footfallNew/footfallSlice";
import {
    selectCatchmentCustomerProfiles,
    selectComparatorsByChapter,
    selectComparatorStores,
    selectPinnedLocation,
    selectTarget,
    selectTargetStoreCategory
} from "modules/customer/tools/location/locationSlice";
import {
    selectModelFeatures,
    selectPeakWeekRevenuePrediction,
    selectPredictionForm,
    selectRevenuePredictionDrivers,
    selectStoreParametersForm,
    selectTypicalWeekRevenuePrediction,
    selectTypicalYearRevenuePrediction,
    selectIsLoading as selectOverviewIsLoading,
    selectHasErrors as selectOverviewHasErrors
} from "modules/customer/tools/location/overview/overviewSlice";
import {
    selectSpendByMarketCategory,
    selectIsLoading as selectSpendIsLoading,
    selectHasErrors as selectSpendHasErrors
} from "modules/customer/tools/location/spend/spendSlice";
import { selectFeatureFlags } from "modules/featureFlags/featureFlagsSlice";
import { apiGet } from "modules/helpers/api/apiSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { notifyError } from "modules/notifications/notificationsSlice";
import { RootState } from "store";

//@ts-ignore
let xlsx: typeof import("@sheet/edit") | null = null;
try {
    xlsx = require("@sheet/edit");
} catch {
    //@ts-ignore
}

interface DataExportState {
    //
}

const initialState: DataExportState = {
    //
};

const dataExportSlice = createSlice({
    name: "customer/tools/location/dataExport",
    initialState,
    reducers: {
        //
    }
});

// export const {
//     //
// } = dataExportSlice.actions;


export const exportData = (): AppThunk => async (dispatch, getState) => {
    try {
        if (xlsx === null) {
            throw new Error("SheetJS not available.");
        }
        const state = getState();

        const templateUrlResponse = await dispatch(apiGet("/api/customer/tools/location/data-export-template"));
        const templateUrl = templateUrlResponse.data.url;
        const template = await (await fetch(templateUrl)).arrayBuffer(); //ToDo: using fetch until we can make apiGet deal with xlsx files

        const workbook = xlsx.read(template, { template: true });

        const pinnedLocation = selectPinnedLocation(state);
        const comparatorsByChapter = selectComparatorsByChapter(state);
        const now = DateTime.now();

        //Controls
        const appDetails = selectAppDetails(state);
        const userInfo = selectUserInfo(state);
        const controlsWorksheetName = "Controls";
        xlsx.utils.template_set_aoa(workbook, controlsWorksheetName, "B1", [[appDetails.versionNumber]]);
        xlsx.utils.template_set_aoa(workbook, controlsWorksheetName, "B2", [[now.toFormat("dd/MM/yyyy HH:mm:ss")]]);
        xlsx.utils.template_set_aoa(workbook, controlsWorksheetName, "B3", [[`${userInfo.firstName} ${userInfo.lastName}`]]);
        xlsx.utils.template_set_aoa(workbook, controlsWorksheetName, "B4", [[userInfo.email]]);

        //Assumptions
        const targetStoreCategory = selectTargetStoreCategory(state);
        const comparatorStores = selectComparatorStores(state);
        const likelihoodOfVisitingThresholds = selectLikelihoodOfVisitingThresholds(state);
        const assumptionsWorksheetName = "Assumptions";
        xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, "B2", [[targetStoreCategory?.name ?? ""]]);
        for (let i = 0; i < comparatorStores.length; i++) {
            const store = comparatorStores[i];
            const index = 3 + i;
            const cell = `B${index}`;
            xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, cell, [[store.name]]);
        }
        xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, "B8", [[`Near ${pinnedLocation?.address}`]]);
        xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, "B9", [[pinnedLocation?.latitude ?? 0]]);
        xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, "B10", [[pinnedLocation?.longitude ?? 0]]);
        xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, "B11", [[likelihoodOfVisitingThresholds[0]]]);
        xlsx.utils.template_set_aoa(workbook, assumptionsWorksheetName, "B12", [[likelihoodOfVisitingThresholds[1]]]);

        //RP_General
        const predictionForm = selectPredictionForm(state);
        const typicalYearRevenuePrediction = selectTypicalYearRevenuePrediction(state);
        const typicalWeekRevenuePrediction = selectTypicalWeekRevenuePrediction(state);
        const peakWeekRevenuePrediction = selectPeakWeekRevenuePrediction(state);
        const rpGeneralWorksheetName = "RP_General";
        xlsx.utils.template_set_aoa(workbook, rpGeneralWorksheetName, "B2", [[predictionForm.group]]);
        xlsx.utils.template_set_aoa(workbook, rpGeneralWorksheetName, "B3", [[predictionForm.model]]);
        xlsx.utils.template_set_aoa(workbook, rpGeneralWorksheetName, "B4", [[typicalYearRevenuePrediction]]);
        xlsx.utils.template_set_aoa(workbook, rpGeneralWorksheetName, "B5", [[typicalWeekRevenuePrediction]]);
        xlsx.utils.template_set_aoa(workbook, rpGeneralWorksheetName, "B6", [[peakWeekRevenuePrediction]]);

        //RP_Features
        const storeParametersForm = selectStoreParametersForm(state);
        const modelFeatures = selectModelFeatures(state);
        const filledFeatures = Array.from(storeParametersForm).map(([name, value]) => ({ name, value }));
        const features: { name: string, value: string }[] = [];
        features.push({ name: "Sales area size (square feet)", value: predictionForm.salesAreaInSqft });
        features.push(...filledFeatures);
        const getFeatureDisplayName = (name: string) => {
            switch (name) {
                case "CarPark":
                    return "Car park";
                case "ClickAndCollect":
                    return "Click and collect";
                case "ClientDefinedRegion":
                    return "Region";
                case "LocationType":
                    return "Location type";
                case "NumberOfCarParkSpace":
                    return "Number of parking spaces";
                case "NumberOfMannedTills":
                    return "Number of manned tills";
                case "NumberOfSelfCheckout":
                    return "Number of self-checkouts";
                case "OpenHoursWeek":
                    return "Opening hours per week";
                case "Segment":
                    return "Segment";
                case "StoreFormat":
                    return "Format";
                case "TradingFloors":
                    return "Number of trading floors";
                case "Visibility":
                    return "Visibility";
                case "WheelchairAccessible":
                    return "Wheelchair accessible";
                default: {
                    const modelFeature = modelFeatures.find(modelFeature => modelFeature.defaultName === name);
                    return modelFeature
                        ? modelFeature.displayName
                        : name;
                }
            }
        };
        const rpFeaturesWorksheetName = "RP_Features";
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            const index = i + 2;
            const range = `A${index}:B${index}`;
            const featureDisplayName = getFeatureDisplayName(feature.name);
            xlsx.utils.template_set_aoa(workbook, rpFeaturesWorksheetName, range, [[featureDisplayName, feature.value]]);
        }

        //RP_Waterfall
        const revenuePredictionDrivers = selectRevenuePredictionDrivers(state);
        const rpWaterfallWorksheetName = "RP_Waterfall";
        for (let i = 0; i < revenuePredictionDrivers.length; i++) {
            const driver = revenuePredictionDrivers[i];
            const index = i + 2;
            const range = `A${index}:B${index}`;
            xlsx.utils.template_set_aoa(workbook, rpWaterfallWorksheetName, range, [[driver.name, driver.shapValue]]);
        }

        //Demographics
        const catchmentAreaDemographic = selectCatchmentAreaDemographic(state);
        const weightedPopulationInCatchmentArea = selectWeightedPopulationInCatchmentArea(state);
        const totalPopulationInCatchmentArea = selectTotalPopulationInCatchmentArea(state);
        const demographicIndicatorsInCatchmentArea = selectDemographicIndicatorsInCatchmentArea(state);
        const demographicsWorksheetName = "Demographics";
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C2", [[catchmentAreaDemographic.retailCentre.affluence]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D2", [[catchmentAreaDemographic.target.affluence]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C3", [[catchmentAreaDemographic.retailCentre.age]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D3", [[catchmentAreaDemographic.target.age]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C4", [[catchmentAreaDemographic.retailCentre.children]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D4", [[catchmentAreaDemographic.target.children]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C5", [[catchmentAreaDemographic.retailCentre.diversity]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D5", [[catchmentAreaDemographic.target.diversity]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C6", [[catchmentAreaDemographic.retailCentre.urbanicity]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D6", [[catchmentAreaDemographic.target.urbanicity]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C7", [[weightedPopulationInCatchmentArea.retailCentre]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D7", [[weightedPopulationInCatchmentArea.comparator]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C8", [[totalPopulationInCatchmentArea.retailCentre]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D8", [[totalPopulationInCatchmentArea.comparator]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D9", [[comparatorsByChapter?.demographics.storeName]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C10", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age0to16]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D10", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age0to16]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C11", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age17to25]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D11", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age17to25]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C12", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age26to35]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D12", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age26to35]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C13", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age36to45]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D13", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age36to45]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C14", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age46to55]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D14", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age46to55]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C15", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age56to65]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D15", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age56to65]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C16", [[demographicIndicatorsInCatchmentArea.retailCentre?.ageStructureGB?.age66Plus]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D16", [[demographicIndicatorsInCatchmentArea.benchmark?.ageStructureGB?.age66Plus]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C17", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncomeSub20k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D17", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncomeSub20k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C18", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome20to30k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D18", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome20to30k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C19", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome30to40k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D19", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome30to40k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C20", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome40to50k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D20", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome40to50k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C21", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome50to60k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D21", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome50to60k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C22", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome60to70k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D22", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome60to70k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C23", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome70to80k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D23", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome70to80k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C24", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome80to90k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D24", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome80to90k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C25", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome90to100k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D25", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome90to100k]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "C26", [[demographicIndicatorsInCatchmentArea.retailCentre?.householdIncome?.countIncome100kPlus]]);
        xlsx.utils.template_set_aoa(workbook, demographicsWorksheetName, "D26", [[demographicIndicatorsInCatchmentArea.benchmark?.householdIncome?.countIncome90to100k]]);

        //CustomerProfiles
        const catchmentCustomerProfilesDataWrapper = selectCatchmentCustomerProfiles(state);
        const catchmentCustomerProfiles = catchmentCustomerProfilesDataWrapper.data;
        const catchmentCustomerProfileSubgroups = _(catchmentCustomerProfiles)
            .groupBy(catchmentCustomerProfile => catchmentCustomerProfile.subgroupName)
            .map((group, key) => ({
                subgroupName: key,
                numberOfVisitors: _.sum(group.map(item => item.numberOfVisitors))
            }))
            .value();
        const customerProfilesWorksheetName = "CustomerProfiles";
        for (let i = 0; i < catchmentCustomerProfileSubgroups.length; i++) {
            const subgroup = catchmentCustomerProfileSubgroups[i];
            const index = i + 2;
            const range = `A${index}:B${index}`;
            xlsx.utils.template_set_aoa(workbook, customerProfilesWorksheetName, range, [[subgroup.subgroupName, subgroup.numberOfVisitors]]);
        }

        //Spend_Cat
        const spendByMarketCategory = selectSpendByMarketCategory(state);
        const spendCatWorksheetName = "Spend_Cat";
        for (let i = 0; i < spendByMarketCategory.length; i++) {
            const spend = spendByMarketCategory[i];
            const index = i + 2;
            const range = `A${index}:C${index}`;
            xlsx.utils.template_set_aoa(workbook, spendCatWorksheetName, range, [[spend.categoryName, spend.weightedSpend, spend.spendPerHead]]);
        }

        //Competition
        const breakdownOfCompetitorStores = selectBreakdownOfCompetitorStores(state);
        const competitionWorksheetName = "Competition";
        for (let i = 0; i < breakdownOfCompetitorStores.length; i++) {
            const competitor = breakdownOfCompetitorStores[i];
            const index = i + 2;
            const range = `A${index}:D${index}`;
            xlsx.utils.template_set_aoa(workbook, competitionWorksheetName, range, [[competitor.fascia, competitor.size, competitor.distanceToProposedStore, competitor.distanceToHotspot]]);
        }

        //Footfall
        const target = selectTarget(state);
        const lastTwelveMonthsTotalVisits = selectLastTwelveMonthsTotalVisits(state);
        const latestMonthFootfall = selectLatestMonthFootfall(state);
        const footfallWorksheetName = "Footfall";
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "C2", [[pinnedLocation?.retailCentre.footfallCentile ?? 0]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "D2", [[target?.footfall ?? 0]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "D3", [[comparatorsByChapter?.footfall.storeName]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "C4", [[lastTwelveMonthsTotalVisits.selectedStore]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "D4", [[lastTwelveMonthsTotalVisits.comparator]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "C5", [[latestMonthFootfall.numberOfVisits.selectedStore]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "D5", [[latestMonthFootfall.numberOfVisits.comparator]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "C6", [[latestMonthFootfall.dwellTime.selectedStore]]);
        xlsx.utils.template_set_aoa(workbook, footfallWorksheetName, "D6", [[latestMonthFootfall.dwellTime.comparator]]);

        //CannibalisationByStore
        const sharedPopulationByStore = selectSharedPopulationByStore(state);
        const cannibalisationByStoreWorksheetName = "CannibalisationByStore";
        for (let i = 0; i < sharedPopulationByStore.length; i++) {
            const item = sharedPopulationByStore[i];
            const index = i + 2;
            const range = `A${index}:C${index}`;
            xlsx.utils.template_set_aoa(workbook, cannibalisationByStoreWorksheetName, range, [[item.store, item.straightLineDistance, item.sharedCustomers]]);
        }

        //CannibalisationByGroup
        const sharedAndNewCustomersByCustomerProfile = selectSharedAndNewCustomersByCustomerProfile(state);
        const cannibalisationByGroupWorksheetName = "CannibalisationByGroup";
        for (let i = 0; i < sharedAndNewCustomersByCustomerProfile.length; i++) {
            const item = sharedAndNewCustomersByCustomerProfile[i];
            const index = i + 2;
            const range = `A${index}:C${index}`;
            xlsx.utils.template_set_aoa(workbook, cannibalisationByGroupWorksheetName, range, [[item.customerProfile, item.sharedCustomers, item.uniqueGainedPopulation]]);
        }

        xlsx.writeFileXLSX(workbook, `dash-location-data-export-${now.toFormat("yyyy-MM-dd-HHmmss")}.xlsx`, { template: true });
    } catch (error) {
        dispatch(logError("Error exporting Location data.", error));

        dispatch(notifyError("Error exporting Location data."));
    }
};

export const selectCanExportData = createSelector(
    (state: RootState) => selectFeatureFlags(state),
    (state: RootState) => selectTotalPopulationInCatchmentArea(state),
    (state: RootState) => selectWeightedPopulationInCatchmentArea(state),
    (state: RootState) => selectCatchmentCustomerProfiles(state),
    (state: RootState) => selectDemographicsIsLoading(state),
    (state: RootState) => selectDemographicsHasErrors(state),
    (state: RootState) => selectCannibalisationIsLoading(state),
    (state: RootState) => selectCannibalisationHasErrors(state),
    (state: RootState) => selectFootfallIsLoading(state),
    (state: RootState) => selectFootfallHasErrors(state),
    (state: RootState) => selectOverviewIsLoading(state),
    (state: RootState) => selectOverviewHasErrors(state),
    (state: RootState) => selectSpendIsLoading(state),
    (state: RootState) => selectSpendHasErrors(state),
    (featureFlags, totalPopulationInCatchmentArea, weightedPopulationInCatchmentArea, catchmentCustomerProfilesDataWrapper,
        demographicsIsLoading, demographicsHasErrors, cannibalisationIsLoading, cannibalisationHasErrors,
        footfallIsLoading, footfallHasErrors, overviewIsLoading, overviewHasErrors,
        spendIsLoading, spendHasErrors) => {
        const newSpendEnabled = featureFlags.enableCustomerToolsLocationSpendNew;
        const spendNotReady = !newSpendEnabled && (spendIsLoading || spendHasErrors);
        return !(totalPopulationInCatchmentArea.isLoading || totalPopulationInCatchmentArea.hasErrors
            || weightedPopulationInCatchmentArea.isLoading || weightedPopulationInCatchmentArea.hasErrors
            || catchmentCustomerProfilesDataWrapper.isLoading || catchmentCustomerProfilesDataWrapper.hasErrors
            || demographicsIsLoading || demographicsHasErrors || cannibalisationIsLoading || cannibalisationHasErrors
            || footfallIsLoading || footfallHasErrors || overviewIsLoading || overviewHasErrors
            || spendNotReady);
    }
);

export default dataExportSlice;
